import React, { useContext, useEffect, useState } from 'react';
import { Link, graphql, useStaticQuery } from 'gatsby';
import axios from 'axios';
import clsx from 'clsx';
import { MdClose, MdMenu, MdSearch } from 'react-icons/md';
import HeaderStyles from '../styles/HeaderStyles';
import Logo from './Logo';
import ActionButton from './buttons/ActionButton';
import { menu } from '../constants/menu';
import { SearchModalContext } from '../contexts/searchModalContext';

// NEW: Moved the GraphQL query from SearchModal to Header
const searchUrisQuery = graphql`
  {
    localSearchBlogs { publicStoreURL publicIndexURL }
    localSearchCategories { publicStoreURL publicIndexURL }
    localSearchAuthors { publicStoreURL publicIndexURL }
    localSearchActivities { publicStoreURL publicIndexURL }
  }
`;

function Header() {
  const [isNavOpen, setIsNavOpen] = useState(false);
  const { openSearchModal, searchIndexStore, setSearchIndexStore } = useContext(SearchModalContext);
  const data = useStaticQuery(searchUrisQuery); // Fetch endpoints

  useEffect(() => {
    // FIX: Body overflow bug handled correctly with cleanup function
    document.body.style.overflow = isNavOpen ? 'hidden' : 'unset';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isNavOpen]);

  // NEW: Prefetch Logic
  const prefetchSearchData = async () => {
    // Prevent duplicate fetching if already loaded or loading
    if (searchIndexStore) return;

    // Set temporary loading state
    setSearchIndexStore({ isLoading: true });

    try {
      const [
        { data: blogsIndex }, { data: blogsStore },
        { data: categoriesIndex }, { data: categoriesStore },
        { data: authorsIndex }, { data: authorsStore },
        { data: activitiesIndex }, { data: activitiesStore },
      ] = await Promise.all([
        axios.get(data.localSearchBlogs.publicIndexURL),
        axios.get(data.localSearchBlogs.publicStoreURL),
        axios.get(data.localSearchCategories.publicIndexURL),
        axios.get(data.localSearchCategories.publicStoreURL),
        axios.get(data.localSearchAuthors.publicIndexURL),
        axios.get(data.localSearchAuthors.publicStoreURL),
        axios.get(data.localSearchActivities.publicIndexURL),
        axios.get(data.localSearchActivities.publicStoreURL),
      ]);

      // Save to context
      setSearchIndexStore({
        blogs: { index: blogsIndex, store: blogsStore },
        categories: { index: categoriesIndex, store: categoriesStore },
        authors: { index: authorsIndex, store: authorsStore },
        activities: { index: activitiesIndex, store: activitiesStore },
      });
    } catch (error) {
      console.error("Failed to prefetch search indexes:", error);
      setSearchIndexStore(null); // Reset on failure so it can try again
    }
  };

  const handleSearchModalOpen = () => {
    prefetchSearchData(); // Failsafe: Fetch if they clicked without hovering (e.g. mobile tap)
    openSearchModal();
  };

  const handleNavItemClick = () => {
    if (isNavOpen) setIsNavOpen(false);
  };

  // FIX: Helper for keyboard accessibility
  const handleKeydown = (e, action) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      action();
    }
  };

  return (
    <HeaderStyles>
      <div className="container">
        <div className="header__container">
          <div className="logo">
            <Logo />
          </div>
          <div className={clsx('nav__wrapper', isNavOpen && 'open')}>
            
            {/* MOBILE ICONS */}
            <div className="mobileIcon">
              <div className="searchIcon">
                <div
                  className="searchIcon__wrapper"
                  onClick={handleSearchModalOpen}
                  onMouseEnter={prefetchSearchData} // PREFETCH TRIGGER
                  onKeyDown={(e) => handleKeydown(e, handleSearchModalOpen)}
                  tabIndex={0}
                  role="button"
                  aria-label="Open Search"
                >
                  <MdSearch />
                </div>
              </div>
              <ActionButton
                className="mobileMenuBtn"
                onClick={() => setIsNavOpen(true)}
                onKeyDown={(e) => handleKeydown(e, () => setIsNavOpen(true))}
                aria-label="Open Menu"
              >
                <MdMenu />
              </ActionButton>
            </div>

            {/* MOBILE BACKDROP */}
            {isNavOpen && (
              <div
                aria-label="Close Menu"
                role="button"
                tabIndex={0}
                className="mobileNavBg"
                onClick={() => setIsNavOpen(false)}
                onKeyDown={(e) => handleKeydown(e, () => setIsNavOpen(false))}
              />
            )}

            <nav>
              <ActionButton
                className="mobileMenuCloseBtn"
                onClick={() => setIsNavOpen(false)}
                onKeyDown={(e) => handleKeydown(e, () => setIsNavOpen(false))}
                aria-label="Close Menu"
              >
                <MdClose />
              </ActionButton>
              <ul>
                {menu.map((item) => (
                  <li key={item.path}>
                    <Link to={item.path} onClick={handleNavItemClick}>
                      {item.title}
                    </Link>
                  </li>
                ))}
                
                {/* DESKTOP SEARCH ICON */}
                <li className="searchIcon">
                  <div
                    className="searchIcon__wrapper"
                    onClick={handleSearchModalOpen}
                    onMouseEnter={prefetchSearchData} // PREFETCH TRIGGER
                    onKeyDown={(e) => handleKeydown(e, handleSearchModalOpen)}
                    tabIndex={0}
                    role="button"
                    aria-label="Open Search"
                  >
                    <MdSearch />
                  </div>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>
    </HeaderStyles>
  );
}

export default Header;